"use client";

import { motion, AnimatePresence } from "framer-motion";
import { Clock, ChevronDown, ChevronUp } from "lucide-react";
import { useState, useEffect } from "react";

// Sample data structure for library hours
const libraryHours = [
  { day: "Monday", hours: "10:00 AM - 6:00 PM", isOpen: true },
  { day: "Tuesday", hours: "10:00 AM - 6:00 PM", isOpen: true },
  { day: "Wednesday", hours: "10:00 AM - 6:00 PM", isOpen: true },
  { day: "Thursday", hours: "10:00 AM - 6:00 PM", isOpen: true },
  { day: "Friday", hours: "10:00 AM - 5:00 PM", isOpen: true },
  { day: "Saturday", hours: "10:00 AM - 6:00 PM", isOpen: true },
  { day: "Sunday", hours: "10:00 AM - 6:00 PM", isOpen: true },
];

const LibraryHours = () => {
  const [currentDay, setCurrentDay] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  // Get current day on component mount
  useEffect(() => {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    setCurrentDay(days[new Date().getDay()]);
  }, []);

  // Get current day's hours for button display
  const todaySchedule = libraryHours.find(
    (schedule) => schedule.day === currentDay
  );

  // Animation variants
  const containerVariants = {
    collapsed: {
      height: "auto",
      width: "auto",
    },
    expanded: {
      height: "auto",
      width: 320,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  const contentVariants = {
    collapsed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.2,
      },
    },
    expanded: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.3,
        delay: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: (i) => ({
      x: 0,
      opacity: 1,
      transition: {
        delay: i * 0.05,
        duration: 0.2,
      },
    }),
  };

  return (
    <div className="fixed top-24 right-4 z-50">
      <motion.div
        variants={containerVariants}
        animate={isExpanded ? "expanded" : "collapsed"}
        className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
      >
        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full p-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-primary" />
            <span className=" font-medium text-gray-900">Library Hours</span>
          </div>
          <div className="flex items-center space-x-2">
            {!isExpanded && todaySchedule && (
              <span
                className={`text-xs ml-2 px-1 py-0.5 rounded-full text-white  ${
                  todaySchedule.isOpen ? "bg-green-600" : "bg-red-500"
                }`}
              >
                {todaySchedule.isOpen ? "Open" : "Closed"}
              </span>
            )}
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </div>
        </motion.button>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              variants={contentVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              className="border-t border-gray-100"
            >
              <div className="p-3 space-y-2">
                {libraryHours.map((schedule, index) => (
                  <motion.div
                    key={schedule.day}
                    custom={index}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    className={`flex justify-between items-center p-2 rounded-md  transition-colors ${
                      currentDay === schedule.day
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">
                        {schedule.day.slice(0, 3)}
                      </span>
                      {currentDay === schedule.day && (
                        <span className="px-1.5 py-0.5 text-sm rounded bg-primary/20 text-primary">
                          Today
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`text-sm ${
                          schedule.isOpen ? "text-gray-600" : "text-red-500"
                        }`}
                      >
                        {schedule.hours}
                      </span>
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${
                          schedule.isOpen ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                    </div>
                  </motion.div>
                ))}

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-sm text-center text-gray-500 pt-2 border-t border-gray-100"
                >
                  * The library is open every day except government holidays.
                </motion.p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default LibraryHours;
